import { useMutation } from "@apollo/react-hooks";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { Button } from "@/ui";
import { Icon } from "@/components";
import useNotification from "../components/useNotification";
import { formatUpdateOrderInput } from "../../../utils/utils";
import useMeta from "../useMeta";

const SaveIcon = () => <Icon icon="save" />;

const useFormActions = (
    keyField,
    resource,
    createMutation,
    updateMutation,
    refetchQueries,
    options,
    route,
    isValid,
    isSubmited = null,
    color = "primary",
    className,
    textaction,
) => {
    const { isCreate } = useMeta();

    const history = useHistory();

    const notify = useNotification();

    const [mutate, { loading }] = useMutation(
        isCreate ? createMutation : updateMutation,
        {
            refetchQueries,
            onCompleted: resp => {
                notify(
                    `${resource} ${
                        isCreate ? "Created" : "Updated"
                    } Successfully.`,
                    "success",
                );

                const keys = _.keys(resp);
                const entity = resp[keys[0]];

                if (isCreate) {
                    let url = _.replace(route, ":id", entity[keyField]);

                    history.replace(url);
                }
            },
            onError: () => {
                notify(
                    `Error ${
                        isCreate ? "Creating" : "Updating"
                    } The ${resource}.`,
                    "error",
                );
            },
        },
    );

    const createOrUpdate = inputOptions => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            // 1) clone options
            let finalOptions = _.cloneDeep(options);

            // 2) merge override into variables.input (override wins)
            if (
                inputOptions &&
                typeof inputOptions === "object" &&
                finalOptions?.variables?.input
            ) {
                finalOptions.variables.input = {
                    ...(finalOptions.variables.input ?? {}),
                    ...formatUpdateOrderInput(inputOptions),
                };
            }

            if (finalOptions.variables.input) {
                if (finalOptions.variables.input.menu_items) {
                    finalOptions.variables.input.menu_items = _.map(
                        finalOptions.variables.input.menu_items,
                        v => {
                            return _.omit(v, ["__typename"]);
                        },
                    );
                }
                if (finalOptions.variables.input.store_feature) {
                    finalOptions.variables.input.store_feature = _.map(
                        finalOptions.variables.input.store_feature,
                        v => {
                            return _.omit(v, ["__typename"]);
                        },
                    );
                }
                if (
                    finalOptions.variables.input.shipping_setting &&
                    finalOptions.variables.input.shipping_setting
                        .free_shipping_method
                ) {
                    finalOptions.variables.input.shipping_setting.free_shipping_method.regions =
                        _.map(
                            finalOptions.variables.input.shipping_setting
                                .free_shipping_method.regions,
                            v => {
                                return _.omit(v, ["__typename"]);
                            },
                        );
                }
                if (
                    finalOptions.variables.input.shipping_setting &&
                    finalOptions.variables.input.shipping_setting
                        .flat_rate_method
                ) {
                    finalOptions.variables.input.shipping_setting.flat_rate_method.regions =
                        _.map(
                            finalOptions.variables.input.shipping_setting
                                .flat_rate_method.regions,
                            v => {
                                return _.omit(v, ["__typename"]);
                            },
                        );
                }

                if (finalOptions.variables.input.material) {
                    if (finalOptions.variables.input.material.price_table) {
                        finalOptions.variables.input.material.price_table =
                            _.map(
                                finalOptions.variables.input.material
                                    .price_table,
                                p =>
                                    _.omit(p, ["__typename", "value", "label"]),
                            );
                    }

                    if (finalOptions.variables.input.material.colors) {
                        finalOptions.variables.input.material.colors = _.map(
                            finalOptions.variables.input.material.colors,
                            p => _.omit(p, ["__typename", "value", "label"]),
                        );
                    }
                }

                if (finalOptions.variables.input.colors) {
                    finalOptions.variables.input.colors = _.map(
                        finalOptions.variables.input.colors,
                        p => _.omit(p, ["__typename", "value", "label"]),
                    );
                }
            }

            finalOptions = _.omit(finalOptions, [
                "variables.input.__typename",
                "variables.input.delivery_address.__typename",
                //"variables.input.address.__typename",
                "variables.input.seo.__typename",
                "variables.input.ignore_whitespace",
                "variables.input.shipping_setting.free_shipping_method.__typename",
                "variables.input.shipping_setting.flat_rate_method.__typename",
                "variables.input.account_setting.__typename",
                "variables.input.shipping_setting.fulfillment_center.__typename",
                "variables.input.shipping_setting.local_delivery_method.__typename",
                "variables.input.shipping_setting.local_pickup_method.__typename",
                "variables.input.aws_pinpoint_analytics.__typename",
                "variables.input.material.__typename",
                "variables.input.max_height",
                "variables.input.max_width",
            ]);

            mutate(finalOptions);
        }
    };

    const buttonVariant = color === "danger" ? "secondary" : "primary";

    const createOrUpdateButton = (
        <Button
            className={className}
            disabled={loading}
            iconLeft={<SaveIcon />}
            key="update-store"
            loading={loading}
            onClick={() => createOrUpdate()}
            variant={buttonVariant}
        >
            {textaction || (isCreate ? "Create" : "Update")}
        </Button>
    );

    return { createOrUpdateButton, createOrUpdate, loading };
};

export default useFormActions;
